import { MapName } from "../../../../../stateManagement/reducers/maps/actions";
import { ListValuesSource, MapCalendarFilter, MapFiltersConfig, MapListCheckboxFilter, MapListRadioFilter, MapTreeCheckboxFilter } from "./types";
import { useMapDispatch } from "../../../../../stateManagement/hooks/useMapDispatch";
import { configureTreeCheckboxNodes, countAppliedFiltersFromSelectedValues, getAppliedValuesFromSelectedValues } from "./treeCheckbox/filterTreeCheckboxFunctions";
import { configureCalendarHintText, configureCalendarValues } from "./calendar/filterCalendarFunctions";
import { configureListRadioPresetValues } from "./listRadio/filterListRadioFunctions";
import { formatDateToISO8601 } from "src/shared/format/date";

function isListCheckbox(
    f: MapTreeCheckboxFilter | MapListCheckboxFilter | MapCalendarFilter,
): f is MapListCheckboxFilter {
    return f.format === 'list-checkbox';
}

function isTreeCheckbox(
    f: MapTreeCheckboxFilter | MapListCheckboxFilter | MapCalendarFilter,
): f is MapTreeCheckboxFilter {
    return f.format === 'tree-checkbox';
}

function isListRadio(
    f: MapTreeCheckboxFilter | MapListCheckboxFilter | MapCalendarFilter,
): f is MapListRadioFilter {
    return f.format === 'list-radio';
}

function isCalendar(
    f: MapTreeCheckboxFilter | MapListCheckboxFilter | MapCalendarFilter,
): f is MapCalendarFilter {
    return f.format === 'calendar';
}

export function configureMapFiltersStateFromConfig(filtersConfig: MapFiltersConfig) {
    let mapFiltersState = {}
    filtersConfig.map(filterConfig => {
        let { fieldName, type, format, operation, canBeIgnored, canIncludeNull, includeNull, preset } = filterConfig ?? {}
        let filterState: any = {
            type,
            format,
            operation,
            ...(canBeIgnored ? { isIgnored: true } : {}),
            ...(canIncludeNull ? { includeNull } : {}),
            ...(canIncludeNull ? { canIncludeNull } : {}),
        }
        if (isListCheckbox(filterConfig)) {
            const { values, hasSearch } = filterConfig
            filterState = {
                ...filterState,
                // If values is an object, treat as dynamic source; initialize as empty until loaded
                values: Array.isArray(values) ? values : undefined,
                ...(values && !Array.isArray(values) ? { valuesSource: values as ListValuesSource } : {}),
                ...(hasSearch ? { searchValue: '' } : {}),
            }
        }
        if (isTreeCheckbox(filterConfig)) {
            let { nodes: rawNodes, hasSearch } = filterConfig
            const { nodes, selectedValues } = configureTreeCheckboxNodes(rawNodes)
            filterState = {
                ...filterState,
                nodes,
                selectedValues,
                ...(hasSearch ? { searchValue: '' } : {}),
            }
        }
        if (isListRadio(filterConfig)) {
            let { values, hasSearch } = filterConfig
            if (preset) values = configureListRadioPresetValues(preset)
            filterState = {
                ...filterState,
                values,
                ...(hasSearch ? { searchValue: '' } : {}),
            }
        }
        if (isCalendar(filterConfig)) {
            const { option, preset: presetValues, minDate, maxDate } = filterConfig
            const { maxValue, minValue, value } = configureCalendarValues(operation)
            const hintText = configureCalendarHintText(option)
            filterState = {
                ...filterState,
                ...(value ? { value } : {}),
                ...(minValue ? { minValue } : {}),
                ...(maxValue ? { maxValue } : {}),
                ...(option ? { option } : {}),
                ...(minDate ? { minDate } : {}),
                ...(maxDate ? { maxDate } : {}),
                ...(operation === "between" ? { hintText } : {}),
                ...(presetValues ? { presetValues } : {}),
            }
        }
        // if (type === "input") {
        //     filterState = {
        //         ...filterState,
        //         value: null
        //     }
        // }
        mapFiltersState = { ...mapFiltersState, [fieldName]: filterState }
    })
    return mapFiltersState
}

export function configureAppliedFiltersFromMapFiltersState(mapFiltersState: any) {
    let appliedFilters = {}
    Object.entries(mapFiltersState).map(([fieldName, filterState]: any) => {
        let { type, format, includeNull, operation, isIgnored, canIncludeNull } = filterState ?? {}
        let appliedFilter: any = {
            type,
            format,
            operation,
            ...(includeNull ? { includeNull } : {}),
            ...(isIgnored ? { isIgnored } : {}),
            ...(canIncludeNull ? { canIncludeNull } : {}),
        }
        if (format === 'list-checkbox') {
            let { values } = filterState
            const valuesArray = Array.isArray(values) ? values : [];
            const appliedValues = valuesArray.reduce((acc: any, item: any) => {
                const values = Array.isArray(item.value) ? item.value : [item.value];
                values.forEach(v => {
                    acc[v] = item.checked === true;
                });
                return acc;
            }, {})
            appliedFilter = {
                ...appliedFilter,
                appliedValues: appliedValues,
            }
        }
        if (format === 'list-radio') {
            let { values } = filterState
            const valuesArray = Array.isArray(values) ? values : [];
            const appliedValues = valuesArray.reduce((acc: any, item: any) => {
                const values = Array.isArray(item.value) ? item.value : [item.value];
                values.forEach(v => {
                    acc[v] = item.checked === true;
                });
                return acc;
            }, {})
            appliedFilter = {
                ...appliedFilter,
                appliedValues: appliedValues,
            }
        }
        if (format === 'tree-checkbox') {
            let { nodes, selectedValues } = filterState
            const appliedValues = getAppliedValuesFromSelectedValues(nodes, selectedValues)
            appliedFilter = {
                ...appliedFilter,
                appliedValues: appliedValues,
            }
        }
        if (format === 'calendar') {
            const { value, minValue, maxValue } = filterState
            appliedFilter = {
                ...appliedFilter,
                ...(value ? { value } : {}),
                ...(minValue ? { minValue } : {}),
                ...(maxValue ? { maxValue } : {}),
                ...(isIgnored ? { isIgnored } : {}),
            }
        }
        // if (type === "input") {
        //     filterState = {
        //         ...filterState,
        //         value: null
        //     }
        // }
        appliedFilters = { ...appliedFilters, [fieldName]: appliedFilter }
    })
    return appliedFilters
}

type AppliedFilter = {
    type: 'value' | string;
    format: 'list-checkbox' | 'tree-checkbox' | 'list-radio' | 'calendar' | string;
    operation: 'equals' | 'includes' | 'between' | string;
    appliedValues?: Record<string, boolean>;
    minValue?: Date;
    maxValue?: Date;
    includeNull?: boolean;
    canIncludeNull?: boolean;
    isIgnored?: boolean;
};

export function configureMapFiltersExpressionsFromAppliedFilters(appliedFilters: Record<string, AppliedFilter>): Record<string, string> {
    return Object.entries(appliedFilters).reduce<Record<string, string>>(
        (acc, [fieldName, filter]) => {
            if (filter.type !== 'value') return acc;

            const nullPart = (withNull?: boolean) => (withNull ? ` or ${fieldName} IS NULL` : '');
            const emptyFilter = (withNull?: boolean) =>
                withNull ? `(${fieldName} IS NULL)` : `(${fieldName} = 1 and ${fieldName} <> 1)`;

            const set = (expr: string) => {
                if (expr) acc[fieldName] = expr;
            };

            switch (filter.format) {
                case 'list-checkbox':
                case 'tree-checkbox':
                case 'list-radio': {
                    const {
                        appliedValues: appliedValuesObject = {},
                        includeNull,
                        canIncludeNull,
                        isIgnored,
                        operation,
                    } = filter;

                    const activeValues = Object.entries(appliedValuesObject)
                        .filter(([, checked]) => checked)
                        .map(([v]) => v);
                    const allValues = Object.keys(appliedValuesObject);

                    const inExpr = (values: string[], withNull?: boolean) =>
                        `(${fieldName} in (${values.join(',')})${nullPart(withNull)})`;
                    const likeExpr = (values: string[], withNull?: boolean) =>
                        `(${values.map(v => `${fieldName} LIKE '%,${v},%'`).join(' OR ')}${nullPart(withNull)})`;

                    if (operation === 'equals') {
                        if (!isIgnored) {
                            set(activeValues.length ? inExpr(activeValues, includeNull) : emptyFilter(includeNull));
                        } else {
                            if (allValues.length) set(inExpr(allValues, canIncludeNull));
                        }
                    }

                    if (operation === 'includes') {
                        if (!isIgnored) {
                            set(activeValues.length ? likeExpr(activeValues, includeNull) : emptyFilter(includeNull));
                        } else {
                            if (allValues.length) set(likeExpr(allValues, canIncludeNull));
                        }
                    }
                    break;
                }

                case 'calendar': {
                    if (
                        filter.operation === 'between' &&
                        filter.minValue &&
                        filter.maxValue &&
                        !filter.isIgnored
                    ) {
                        const min = formatDateToISO8601(filter.minValue);
                        const max = formatDateToISO8601(filter.maxValue);
                        set(`${fieldName}>=${min} and ${fieldName}<=${max}`);
                    }
                    break;
                }
            }
            return acc;
        }, {},);
}


export function configureMapFiltersActiveTabsFromConfig(filtersConfig: MapFiltersConfig) {
    return filtersConfig.reduce<number[]>((acc, filter, idx) => {
        if (filter.tabIsActive) acc.push(idx);
        return acc;
    }, []);
}

// >> Функции подсчета активных фильтров

export function countAllAppliedFilters(mapFiltersState: any) {
    if (!mapFiltersState) return null
    let allAppliedFiltersNumber: any = null;
    Object.values(mapFiltersState).map((filterState: any) => {
        const { format, isIgnored } = filterState
        if (format === "tree-checkbox" && !isIgnored) allAppliedFiltersNumber += countFilterTreeCheckboxAppliedFilters(filterState)
        if (format === "list-radio" && !isIgnored) allAppliedFiltersNumber += countFilterListRadioAppliedFilters(filterState)
        if (format === "list-checkbox" && !isIgnored) allAppliedFiltersNumber += countFilterListCheckboxAppliedFilters(filterState)
    })
    return allAppliedFiltersNumber
}

export function countFilterListCheckboxAppliedFilters(filterState: any) {
    let appliedFiltersNumber = 0;
    const { values, includeNull } = filterState ?? {}
    if (includeNull) appliedFiltersNumber++;
    appliedFiltersNumber += values.filter((valueObject: any) => valueObject.checked).length
    return appliedFiltersNumber
}

export function countFilterListRadioAppliedFilters(filterState: any) {
    let appliedFiltersNumber = 0;
    const { includeNull } = filterState ?? {}
    if (includeNull) appliedFiltersNumber++;
    appliedFiltersNumber++;
    return appliedFiltersNumber
}

export function countFilterTreeCheckboxAppliedFilters(filterState: any) {
    let appliedFiltersNumber = 0;
    const { nodes, selectedValues, includeNull } = filterState ?? {}
    if (includeNull) appliedFiltersNumber++;
    appliedFiltersNumber += countAppliedFiltersFromSelectedValues(nodes, selectedValues)
    return appliedFiltersNumber
}

export function countFilterCalendarAppliedFilters(filterState: any) {
    let appliedFiltersNumber = 0;
    const { minValue, maxValue, value } = filterState ?? {}
    if ((minValue && maxValue) || value) appliedFiltersNumber += 1;
    return appliedFiltersNumber
}

// >> Функции очистки фильтров

export function clearSingleFilter(
    event: any,
    fieldName: string,
    mapName: MapName,
    dispatchMap: ReturnType<typeof useMapDispatch>
) {
    event.stopPropagation()
    event.preventDefault()
    dispatchMap(mapName, 'resetSingleMapFilter', fieldName)
}